package com.example.mystybag.login.views.views

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import androidx.compose.ui.Alignment
import kotlinx.coroutines.delay
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.border
import androidx.compose.ui.unit.dp
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.login.views.components.MerchantRegistrationProgressBar
import com.example.mystybag.login.views.components.MerchantRegistrationProgressFractions
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.setSoftInputModeOverlay
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p36
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.login.logic.MerchantRegistrationAddCompanyAddressViewModel

@Composable
fun MerchantRegistrationAddCompanyAddressView(viewModel: MerchantRegistrationAddCompanyAddressViewModel) {
    DisposableEffect(Unit) {
        setSoftInputModeOverlay(overlay = true)
        onDispose { setSoftInputModeOverlay(overlay = false) }
    }
    LaunchedEffect(Unit) {
        delay(150)
        setSoftInputModeOverlay(overlay = true)
    }

    if (PlatformHelper.isWeb) {
        MerchantRegistrationAddCompanyAddressWebView(viewModel)
        return
    }
    BottomSheetUIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        bottomSheetFactory = {}
    ) { _ ->
        MerchantRegistrationAddCompanyAddressContent(viewModel)
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyAddressContent(
    viewModel: MerchantRegistrationAddCompanyAddressViewModel
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .fillMaxWidth()
            .keyboardPadding()
    ) {
    Column(
        modifier = Modifier
            .weight(1f)
            .fillMaxWidth()
            .fillMaxHeight()
            .verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(0.dp)
    ) {
            MerchantRegistrationAddCompanyAddressScrollableBody(viewModel)
        }
        MerchantRegistrationAddCompanyAddressFooter(viewModel)
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyAddressScrollableBody(
    viewModel: MerchantRegistrationAddCompanyAddressViewModel
) {
    val state by viewModel.uiState.subscribeAsState()
    MerchantRegistrationProgressBar(MerchantRegistrationProgressFractions.RESPONSIBLE_PERSONS)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
    ) {
        Spacer(modifier = Modifier.height(p36))

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .then(
                    if (state.showMolListError) {
                        Modifier
                            .border(2.dp, AppTheme.current.colors.error, RoundedCornerShape(20.dp))
                            .padding(p16)
                    } else {
                        Modifier
                    }
                )
        ) {
            Texts.DisplaySmall(
                text = stringResource(SR.strings.mol),
                modifier = Modifier.fillMaxWidth()
            )
            if (state.showMolListError) {
                Spacer(modifier = Modifier.height(p8))
                Texts.BodySmall(
                    text = stringResource(SR.strings.please_enter_all_required_fields),
                    color = AppTheme.current.colors.error,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }

        Spacer(modifier = Modifier.height(p32))

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p24)
        ) {
            state.signatories.forEachIndexed { index, signatory ->
                MolCard(
                    name = "${signatory.firstName} ${signatory.lastName}".trim().ifEmpty { "—" },
                    phone = "${signatory.dialCode}${signatory.phoneNumber}".trim(),
                    onEdit = { viewModel.startEditingSignatory(index) },
                    onDelete = { viewModel.requestDeleteSignatory(index) }
                )
            }
        }
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyAddressFooter(
    viewModel: MerchantRegistrationAddCompanyAddressViewModel
) {
    val state by viewModel.uiState.subscribeAsState()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
    ) {
        if (state.signatories.isNotEmpty()) {
            DefaultButton(
                modifier = Modifier.fillMaxWidth(),
                type = DefaultButtonType.SECONDARY,
                caption = stringResource(SR.strings.add_another_mol),
                callback = { viewModel.openAddMol() }
            )
        }
        Spacer(modifier = Modifier.height(p32))
        DefaultButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = p8),
            type = DefaultButtonType.PRIMARY,
            caption = stringResource(SR.strings.next),
            callback = viewModel::onContinue
        )
    }
}

@Composable
private fun MolCard(
    name: String,
    phone: String,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    val colors = AppTheme.current.colors
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(20.dp))
            .background(colors.surfaceVariant)
            .padding(p20),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(p4)
        ) {
            Texts.TitleMedium(
                text = name,
                color = colors.onSurface,
                modifier = Modifier.fillMaxWidth()
            )
            Texts.BodyMedium(
                text = phone,
                color = colors.onSurfaceVariant,
                modifier = Modifier.fillMaxWidth()
            )
        }
        Row(
            horizontalArrangement = Arrangement.spacedBy(p12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .clip(CircleShape)
                    .background(colors.primary)
                    .clickable(onClick = onEdit),
                contentAlignment = Alignment.Center
            ) {
                Image(
                    painter = painterResource(SRC.images.mol_edit_icon),
                    modifier = Modifier.size(20.dp)
                )
            }
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .clip(CircleShape)
                    .background(colors.error)
                    .clickable(onClick = onDelete),
                contentAlignment = Alignment.Center
            ) {
                Image(
                    painter = painterResource(SRC.images.mol_trash_icon),
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
}
